// src/core/text.js
import { MF_EASINGS } from "./easings.js";

/* =====================================================
   MotionFlow Text
===================================================== */

/* -----------------------------
   Utils
----------------------------- */
function toBool(val, fallback = false) {
  if (val === undefined || val === null || val === "") return fallback;
  return ["true", "1", "yes"].includes(String(val).toLowerCase());
}

function num(val, fallback) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

/* =====================================================
   ACCESSIBILITY
===================================================== */
const reduceMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)"
).matches;

/* =====================================================
   INTERNAL STATE
===================================================== */
const textInstances = new Set();
let lastOptions = {};
let isInitialized = false;

/* =====================================================
   LOOP TEXT
===================================================== */
function initLoop(el, cfg = {}) {
  const children = Array.from(el.children).filter(
    c => c.textContent.trim().length
  );
  if (!children.length) return null;

  // Reduced motion → show first item only
  if (reduceMotion) {
    children.forEach((c, i) => {
      c.style.display = i === 0 ? "" : "none";
    });
    return { destroy() {} };
  }

  const interval = num(
    el.dataset.mfTextLoopInterval,
    cfg.interval ?? 2000
  );

  const animation =
    el.dataset.mfTextLoopAnimation ??
    cfg.animation ??
    "fade-up";

  const duration = num(
    el.dataset.mfTextLoopDuration,
    cfg.duration ?? 500
  );

  const easingName =
    el.dataset.mfTextLoopEasing ??
    cfg.easing ??
    "ease";

  const easing = MF_EASINGS[easingName] || easingName;

  const loopDistance =
    el.dataset.mfTextLoopDistance ??
    cfg.distance ??
    40;
  

  let index = 0;
  let timer = null;

  children.forEach((child, i) => {
    child.style.whiteSpace = "nowrap";
    child.style.display = i === 0 ? "inline-block" : "none";

    child.setAttribute("data-mf-animation", animation);
    child.style.setProperty("--mf-duration", duration + "ms");
    child.style.setProperty("--mf-easing", easing);

    if (loopDistance != null) {
      child.style.setProperty("--mf-distance", loopDistance + "px");
    }

    child.classList.remove("mf-animate");
  });

  requestAnimationFrame(() => {
    children[0].classList.add("mf-animate");
  });

  function showNext() {
    const current = children[index];
    current.classList.remove("mf-animate");
    current.style.display = "none";

    index = (index + 1) % children.length;
    const next = children[index];

    next.style.display = "inline-block";
    next.style.transition = "none";
    next.classList.remove("mf-animate");
    void next.offsetHeight;
    next.style.transition = "";
    next.classList.add("mf-animate");
  }

  timer = setInterval(showNext, interval);

  return {
    destroy() {
      clearInterval(timer);
      children.forEach(child => {
        child.style.display = "";
        child.style.whiteSpace = "";
        child.classList.remove("mf-animate");
        child.style.removeProperty("--mf-duration");
        child.style.removeProperty("--mf-easing");
        child.style.removeProperty("--mf-distance");
        child.removeAttribute("data-mf-animation");
      });
    }
  };
}

/* =====================================================
   TYPING TEXT
===================================================== */
function initTyping(el, cfg = {}) {
  const originalHTML = el.innerHTML;

  const items = Array.from(el.children)
    .map(c => c.textContent.trim())
    .filter(Boolean);

  if (!items.length) return null;

  if (reduceMotion) {
    el.textContent = items[0];
    return { destroy() { el.innerHTML = originalHTML; } };
  }

  const typeSpeed = num(
    el.dataset.mfTextTypingSpeed,
    cfg.speed ?? 80
  );

  const deleteSpeed = num(
    el.dataset.mfTextTypingDeleteSpeed,
    cfg.deleteSpeed ?? 40
  );

  const interval = num(
    el.dataset.mfTextTypingInterval,
    cfg.interval ?? 1200
  );

  const loop = toBool(
    el.dataset.mfTextTypingLoop,
    cfg.loop ?? true
  );

  const showCursor = toBool(
    el.dataset.mfTextTypingCursor,
    cfg.cursor ?? true
  );

  const cursorChar =
    el.dataset.mfTextTypingCursorChar ??
    cfg.cursorChar ??
    "|";

  const blinkCursor = toBool(
    el.dataset.mfTextTypingCursorBlink,
    cfg.cursorBlink ?? true
  );

  let textIndex = 0;
  let charIndex = 0;
  let deleting = false;
  let timeoutId = null;

  el.innerHTML = "";

  const sr = document.createElement("span");
  sr.className = "mf-sr-only";
  sr.setAttribute("aria-live", "polite");
  sr.setAttribute("aria-atomic", "true");
  sr.textContent = items[0];

  const visual = document.createElement("span");
  visual.setAttribute("aria-hidden", "true");

  const textNode = document.createTextNode("");
  visual.appendChild(textNode);

  let cursorEl = null;

  if (showCursor) {
    cursorEl = document.createElement("span");
    cursorEl.className = "mf-typing-cursor";
    cursorEl.textContent = cursorChar;
    cursorEl.setAttribute("aria-hidden", "true");

    if (!blinkCursor) {
      cursorEl.classList.add("mf-typing-cursor-static");
    }

    visual.appendChild(cursorEl);
  }

  el.appendChild(sr);
  el.appendChild(visual);

  function tick() {
    const current = items[textIndex];

    if (!deleting) {
      charIndex++;
      textNode.textContent = current.slice(0, charIndex);

      if (charIndex === current.length) {
        sr.textContent = current;

        if (!loop && textIndex === items.length - 1) {
          if (cursorEl) cursorEl.style.display = "none";
          return;
        }

        timeoutId = setTimeout(() => {
          deleting = true;
          tick();
        }, interval);
        return;
      }
    } else {
      charIndex--;
      textNode.textContent = current.slice(0, charIndex);

      if (charIndex === 0) {
        deleting = false;
        textIndex++;
        if (!loop && textIndex >= items.length) return;
        textIndex %= items.length;
      }
    }

    timeoutId = setTimeout(
      tick,
      deleting ? deleteSpeed : typeSpeed
    );
  }

  tick();

  return {
    destroy() {
      clearTimeout(timeoutId);
      el.innerHTML = originalHTML;
    }
  };
}

/* =====================================================
   MAIN INIT
===================================================== */
export function MotionFlowText(options = {}) {
  lastOptions = options;

  if (isInitialized) {
    destroy();
  }

  isInitialized = true;

  const loopCfg   = options.loop   || {};
  const typingCfg = options.typing || {};

  document.querySelectorAll("[data-mf-text-type]").forEach(el => {
    if (el.__mfTextInited) return;
    el.__mfTextInited = true;

    let instance = null;

    if (el.dataset.mfTextType === "loop") {
      instance = initLoop(el, loopCfg);
    }

    if (el.dataset.mfTextType === "typing") {
      instance = initTyping(el, typingCfg);
    }

    if (instance) {
      textInstances.add({ el, instance });
    }
  });

  return { refresh, destroy };
}

/* =====================================================
   LIFECYCLE
===================================================== */
function refresh() {
  MotionFlowText(lastOptions);
}

function destroy() {
  textInstances.forEach(({ el, instance }) => {
    instance.destroy?.();
    delete el.__mfTextInited;
  });
  textInstances.clear();
  isInitialized = false;
}
